/**
 * Global Upgrade Modal context.
 *
 * When user clicks "Upgrade Now", the modal calls POST v1/common/subscription/upgrade-email/
 * then any optional callback passed to openUpgradeModal(), then closes.
 *
 * Usage from any component:
 *   const { openUpgradeModal, closeUpgradeModal } = useUpgradeModal();
 *   openUpgradeModal();                    // open (no callback)
 *   openUpgradeModal(() => { ... });       // open with optional "Upgrade Now" callback
 *   closeUpgradeModal();                   // close programmatically
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useUpgradeEmailMutation } from "../Services/settingsAPIs/CommonAPI";

export type UpgradeModalOnUpgrade = () => void;

interface UpgradeModalContextValue {
  openUpgradeModal: (onUpgradeClick?: UpgradeModalOnUpgrade) => void;
  closeUpgradeModal: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(
  null
);

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.45)",
};

const contentStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: "24px",
  maxWidth: "400px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
};

const messageStyle: React.CSSProperties = {
  margin: "0 0 20px",
  fontSize: "16px",
  color: "#212529",
  lineHeight: 1.5,
};

const buttonRowStyle: React.CSSProperties = { display: "flex", gap: "10px" };

const maybeLaterButtonStyle: React.CSSProperties = {
  padding: "10px 20px",
  fontSize: "15px",
  fontWeight: 600,
  color: "#495057",
  backgroundColor: "#f1f3f5",
  border: "1px solid #dee2e6",
  borderRadius: "8px",
  cursor: "pointer",
  flex: 1,
};

const upgradeNowButtonStyle: React.CSSProperties = {
  padding: "10px 20px",
  fontSize: "15px",
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "#6366f1",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  flex: 1,
};

function UpgradeModalInner({
  isOpen,
  onClose,
  onUpgradeNowClick,
  isUpgradeLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeNowClick: () => void | Promise<void>;
  isUpgradeLoading: boolean;
}) {
  const handleUpgradeClick = useCallback(() => {
    void onUpgradeNowClick();
  }, [onUpgradeNowClick]);

  if (!isOpen) return null;

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-message"
    >
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <p id="upgrade-modal-message" style={messageStyle}>
          Upgrade your current plan to access this feature.
        </p>
        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={onClose}
            style={maybeLaterButtonStyle}
          >
            Maybe Later
          </button>
          <button
            type="button"
            onClick={handleUpgradeClick}
            disabled={isUpgradeLoading}
            style={{
              ...upgradeNowButtonStyle,
              opacity: isUpgradeLoading ? 0.7 : 1,
              cursor: isUpgradeLoading ? "wait" : "pointer",
            }}
          >
            {isUpgradeLoading ? "..." : "Upgrade Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UpgradeModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [onUpgradeClick, setOnUpgradeClick] = useState<
    UpgradeModalOnUpgrade | undefined
  >(undefined);

  const [triggerUpgradeEmail, { isLoading: isUpgradeLoading }] =
    useUpgradeEmailMutation();

  const openUpgradeModal = useCallback(
    (onUpgrade?: UpgradeModalOnUpgrade) => {
      setOnUpgradeClick(onUpgrade ?? undefined);
      setIsOpen(true);
    },
    []
  );

  const closeUpgradeModal = useCallback(() => {
    setIsOpen(false);
    setOnUpgradeClick(undefined);
  }, []);

  const handleUpgradeNowClick = useCallback(async () => {
    try {
      await triggerUpgradeEmail(undefined).unwrap();
      toast.success(
        "We received your request. Our team will connect with you shortly."
      );
    } catch {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    onUpgradeClick?.();
    closeUpgradeModal();
  }, [triggerUpgradeEmail, onUpgradeClick, closeUpgradeModal]);

  const value: UpgradeModalContextValue = {
    openUpgradeModal,
    closeUpgradeModal,
  };

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <UpgradeModalInner
        isOpen={isOpen}
        onClose={closeUpgradeModal}
        onUpgradeNowClick={handleUpgradeNowClick}
        isUpgradeLoading={isUpgradeLoading}
      />
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext);
  if (ctx == null) {
    throw new Error(
      "useUpgradeModal must be used within an UpgradeModalProvider"
    );
  }
  return ctx;
}
